import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import { useCurrentModule } from '@/hooks/useCurrentModule';
import { supabase } from '@/integrations/supabase/client';
import { Clock, CheckCircle } from 'lucide-react';
import { format, startOfDay } from 'date-fns';
import { triggerMiniConfetti, celebrateOnce } from '@/lib/confetti';

export function DailyTimeGoalWidget() {
  const { user } = useAuth();
  const moduleType = useCurrentModule();
  const [todayMinutes, setTodayMinutes] = useState(0);
  const [goalMinutes, setGoalMinutes] = useState(60);
  const [loading, setLoading] = useState(true);
  // confetti now guarded by celebrateOnce(localStorage)

  useEffect(() => {
    if (!user) return;

    const fetchData = async () => {
      const today = format(startOfDay(new Date()), 'yyyy-MM-dd');

      // Fetch personalized goal for this module
      const { data: goalData } = await supabase
        .from('user_daily_goals')
        .select('target_value')
        .eq('user_id', user.id)
        .eq('goal_type', 'study_time')
        .eq('module_type', moduleType)
        .maybeSingle();

      if (goalData) {
        setGoalMinutes(goalData.target_value);
      }

      // Fetch today's study time from pomodoro sessions for this module
      const { data: sessions } = await supabase
        .from('pomodoro_sessions')
        .select('total_minutes')
        .eq('user_id', user.id)
        .eq('module_type', moduleType)
        .eq('session_date', today);

      const totalMinutes = sessions?.reduce((sum, s) => sum + (s.total_minutes || 0), 0) || 0;
      setTodayMinutes(totalMinutes);
      setLoading(false);

      // Trigger confetti when goal is reached (once per user/module/day)
      if (totalMinutes >= goalMinutes) {
        const dayKey = format(startOfDay(new Date()), 'yyyy-MM-dd');
        celebrateOnce(`daily-time:${user.id}:${moduleType}:${dayKey}`, triggerMiniConfetti);
      }
    };

    fetchData();
  }, [user, goalMinutes, moduleType]);

  const percentage = Math.min((todayMinutes / goalMinutes) * 100, 100);
  const isCompleted = todayMinutes >= goalMinutes;

  // SVG circle dimensions - responsive
  const size = 64;
  const sizeMd = 80;
  const strokeWidth = 5;
  const radius = (size - strokeWidth) / 2;
  const radiusMd = (sizeMd - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const circumferenceMd = 2 * Math.PI * radiusMd;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  const strokeDashoffsetMd = circumferenceMd - (percentage / 100) * circumferenceMd;

  return (
    <Card className="h-[130px] md:min-h-[160px] md:h-full flex flex-col overflow-hidden border-l-4 border-l-orange-500 bg-gradient-to-r from-orange-500/5 to-transparent transition-all duration-200 hover:scale-[1.02] hover:shadow-lg hover:-translate-y-1">
      <CardHeader className="pb-1 p-3 md:p-6 md:pb-1">
        <CardTitle className="text-xs md:text-sm font-medium flex items-center gap-1 md:gap-2">
          <Clock className="h-3 w-3 md:h-4 md:w-4 text-orange-500" />
          Meta de Tempo
        </CardTitle>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col items-center justify-center pt-0 p-3 md:p-6 md:pt-0">
        {loading ? (
          <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-muted animate-pulse" />
        ) : (
          <div className="relative flex items-center justify-center">
            {/* Mobile SVG */}
            <svg 
              width={size} 
              height={size} 
              className="transform -rotate-90 md:hidden"
            >
              <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                stroke="currentColor"
                strokeWidth={strokeWidth}
                fill="none"
                className="text-muted"
              />
              <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                stroke="currentColor"
                strokeWidth={strokeWidth}
                fill="none"
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={strokeDashoffset}
                className={`transition-all duration-500 ${isCompleted ? 'text-green-500' : 'text-orange-500'}`}
              />
            </svg>
            {/* Desktop SVG */}
            <svg 
              width={sizeMd} 
              height={sizeMd} 
              className="transform -rotate-90 hidden md:block"
            >
              <circle
                cx={sizeMd / 2}
                cy={sizeMd / 2}
                r={radiusMd}
                stroke="currentColor"
                strokeWidth={strokeWidth}
                fill="none"
                className="text-muted"
              />
              <circle
                cx={sizeMd / 2}
                cy={sizeMd / 2}
                r={radiusMd}
                stroke="currentColor"
                strokeWidth={strokeWidth}
                fill="none"
                strokeLinecap="round"
                strokeDasharray={circumferenceMd}
                strokeDashoffset={strokeDashoffsetMd}
                className={`transition-all duration-500 ${isCompleted ? 'text-green-500' : 'text-orange-500'}`}
              />
            </svg>
            {/* Center content */}
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              {isCompleted ? (
                <CheckCircle className="h-5 w-5 md:h-6 md:w-6 text-green-500" />
              ) : (
                <>
                  <span className="text-lg md:text-xl font-bold">{todayMinutes}</span>
                  <span className="text-[9px] md:text-[10px] text-muted-foreground">/{goalMinutes} min</span>
                </>
              )}
            </div>
          </div>
        )}
        <p className="text-[10px] md:text-xs text-muted-foreground mt-1 md:mt-2 text-center">
          {isCompleted ? 'Meta atingida! ⏱️' : `${goalMinutes - todayMinutes} min`}
        </p>
      </CardContent>
    </Card>
  );
}
